import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { finalize, Subscription, timer } from 'rxjs';

import { environment } from '../../../../../environments/environment';
import { OrgSelectorService } from '../../../../core/services/org-selector.service';
import { isSuperAdmin } from '../../../../core/utils/is-super-admin.util';
import { AuthenticationsService } from '../../../../modules/authentications/authentications.service';
import { AuthenticationLoginResponseDto } from '../../../../modules/authentications/dtos/authentication-login-response';
import { PermissionDeniedDialogComponent } from '../../../../modules/general/permission-denied/permission-denied-dialog.component';
import { RouteUtils } from '../../../../shared/utils/route/route.util';

import { ClaimUsageIngestionMockService } from './claim-usage-ingestion.mock.service';
import {
	CLAIM_USAGE_INGESTION_ACTIVE_STATUSES,
	CLAIM_USAGE_INGESTION_FINAL_STATUSES,
	ClaimUsageIngestionAcceptedResponse,
	ClaimUsageIngestionBlobStatus,
	ClaimUsageIngestionErrorItem,
	ClaimUsageIngestionHistoryItem,
	ClaimUsageIngestionJobStatus,
	ClaimUsageIngestionOperator,
	ClaimUsageIngestionPostProcessing,
	ClaimUsageIngestionPreProcessing,
	ClaimUsageIngestionStatusResponse,
	ClaimUsageIngestionUploadDialogResult,
	ClaimUsageIngestionUploadRequest,
} from './claim-usage-ingestion.models';
import {
	ClaimUsageIngestionApiService,
	ClaimUsageIngestionService,
} from './claim-usage-ingestion.service';
import { ClaimUsageIngestionProcessingDetailDialogComponent } from './claim-usage-ingestion-processing-detail-dialog.component';
import { ClaimUsageIngestionUploadDialogComponent } from './claim-usage-ingestion-upload-dialog.component';

export const CLAIM_USAGE_INGESTION_POLLING_INTERVAL_MS = 3000;
export const CLAIM_USAGE_INGESTION_MAX_POLLING_ATTEMPTS = 2400;

@Component({
	providers: [
		ClaimUsageIngestionApiService,
		ClaimUsageIngestionMockService,
		{
			deps: [ClaimUsageIngestionMockService, ClaimUsageIngestionApiService],
			useFactory: (
				mockService: ClaimUsageIngestionMockService,
				apiService: ClaimUsageIngestionApiService,
			): ClaimUsageIngestionService =>
				environment.claimUsageIngestionUseMock ? mockService : apiService,
			provide: ClaimUsageIngestionService,
		},
	],
	selector: 'app-claim-usage-ingestion',
	styleUrls: ['./claim-usage-ingestion.component.scss'],
	templateUrl: './claim-usage-ingestion.component.html',
})
export class ClaimUsageIngestionComponent implements OnDestroy, OnInit {
	blockedUploadMessage = '';
	historyItems: ClaimUsageIngestionHistoryItem[] = [];
	historyLoadError = '';
	isHistoryLoading = false;
	isUploading = false;
	postProcessing: ClaimUsageIngestionPostProcessing | null = null;
	preProcessing: ClaimUsageIngestionPreProcessing | null = null;
	uploadError = '';

	private readonly activeBusinessKeys = new Set<string>();
	private blockedUploadBusinessKey = '';
	private readonly businessKeyByJobId = new Map<string, string>();
	private readonly inFlightPollingByJobId = new Set<string>();
	private readonly pollingAttemptsByJobId = new Map<string, number>();
	private readonly pollingErrorsByJobId = new Map<string, string>();
	private readonly pollingSubscriptionsByJobId = new Map<
		string,
		Subscription
	>();
	private routeOrganizationId = '';
	private routeOrganizationSubscription: Subscription | null = null;

	constructor(
		private readonly activatedRoute: ActivatedRoute,
		private readonly authenticationsService: AuthenticationsService,
		private readonly claimUsageIngestionService: ClaimUsageIngestionService,
		private readonly dialog: MatDialog,
		private readonly orgSelectorService: OrgSelectorService,
		private readonly router: Router,
	) {}

	get hasHistoryItems(): boolean {
		return this.historyItems.length > 0;
	}

	get isJobActive(): boolean {
		return Boolean(
			this.preProcessing &&
				CLAIM_USAGE_INGESTION_ACTIVE_STATUSES.includes(
					this.preProcessing.jobStatus,
				),
		);
	}

	get pollingError(): string {
		return this.getPollingErrorForVisibleJob();
	}

	get shouldShowHistoryEmptyState(): boolean {
		return (
			!this.isHistoryLoading &&
			!this.historyLoadError &&
			this.historyItems.length === 0
		);
	}

	canDownloadHistoryFile(item: ClaimUsageIngestionHistoryItem): boolean {
		return Boolean(
			item.preProcessing.sourceFile ||
				item.preProcessing.jobId ||
				item.preProcessing.fileId ||
				item.preProcessing.storagePath,
		);
	}

	downloadHistoryFile(item: ClaimUsageIngestionHistoryItem): void {
		const sourceFile = item.preProcessing.sourceFile;

		if (sourceFile) {
			this.saveBlob(sourceFile, item.preProcessing.originalFileName);

			return;
		}

		this.claimUsageIngestionService
			.download(item.preProcessing.jobId)
			.subscribe({
				error: (error: unknown) => {
					const errorMessage = this.getDownloadErrorMessage(error);

					if (typeof errorMessage === 'string') {
						this.historyLoadError = errorMessage;

						return;
					}

					void errorMessage.then((message) => {
						this.historyLoadError = message;
					});
				},
				next: (response) => {
					const blob = response.body;

					if (!blob) {
						this.historyLoadError =
							'Nao foi possivel baixar o arquivo de origem desta carga.';

						return;
					}

					this.historyLoadError = '';
					this.saveBlob(
						blob,
						this.getDownloadFileName(
							response,
							item.preProcessing.originalFileName,
						),
					);
				},
			});
	}

	formatBytes(size: number): string {
		if (size < 1024) {
			return `${size} B`;
		}

		if (size < 1024 * 1024) {
			return `${(size / 1024).toFixed(1)} KB`;
		}

		return `${(size / (1024 * 1024)).toFixed(1)} MB`;
	}

	formatDate(value: string): string {
		return new Intl.DateTimeFormat('pt-BR', {
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			month: '2-digit',
			year: 'numeric',
		}).format(new Date(value));
	}

	formatFileName(fileName: string): string {
		return this.normalizeFileName(fileName);
	}

	getHistoryCompetence(item: ClaimUsageIngestionHistoryItem): string {
		return item.preProcessing.competence || '-';
	}

	getHistoryOperator(item: ClaimUsageIngestionHistoryItem): string {
		return item.preProcessing.operator || '-';
	}

	getHistoryResponsible(item: ClaimUsageIngestionHistoryItem): string {
		return (
			item.preProcessing.responsible || item.preProcessing.uploadedBy || '-'
		);
	}

	getStatusLabel(status: ClaimUsageIngestionJobStatus): string {
		switch (status) {
			case ClaimUsageIngestionJobStatus.CREATED:
				return '-';
			case ClaimUsageIngestionJobStatus.QUEUED:
				return 'Na fila';
			case ClaimUsageIngestionJobStatus.PROCESSING:
				return 'Processando';
			case ClaimUsageIngestionJobStatus.COMPLETED:
				return 'Sucesso';
			case ClaimUsageIngestionJobStatus.PARTIALLY_COMPLETED:
				return 'Parcial';
			case ClaimUsageIngestionJobStatus.FAILED:
				return 'Falha';
			case ClaimUsageIngestionJobStatus.UNKNOWN:
				return 'Status desconhecido';
			default:
				return 'Status desconhecido';
		}
	}

	getStatusPillClass(status: ClaimUsageIngestionJobStatus): string {
		switch (status) {
			case ClaimUsageIngestionJobStatus.CREATED:
				return 'status-pill-empty';
			case ClaimUsageIngestionJobStatus.FAILED:
				return 'status-pill-error';
			case ClaimUsageIngestionJobStatus.PROCESSING:
				return 'status-pill-processing';
			case ClaimUsageIngestionJobStatus.PARTIALLY_COMPLETED:
				return 'status-pill-warning';
			case ClaimUsageIngestionJobStatus.QUEUED:
				return 'status-pill-waiting';
			default:
				return 'status-pill-success';
		}
	}

	ngOnDestroy(): void {
		this.routeOrganizationSubscription?.unsubscribe();
		this.stopAllPolling();
	}

	ngOnInit(): void {
		if (!this.canAccessClaimUsageIngestion()) {
			this.blockUnauthorizedAccess();

			return;
		}

		this.watchOrganizationRouteContext();
	}

	openHistoryDetail(item: ClaimUsageIngestionHistoryItem): void {
		if (this.shouldLoadErrors(item)) {
			this.claimUsageIngestionService
				.getErrors(item.preProcessing.jobId)
				.subscribe({
					error: () => this.openHistoryDetailDialog(item),
					next: (errorsResponse) =>
						this.openHistoryDetailDialog(item, errorsResponse.items),
				});

			return;
		}

		this.openHistoryDetailDialog(item);
	}

	openProcessingDetail(): void {
		if (!this.preProcessing) {
			return;
		}

		this.dialog.open(ClaimUsageIngestionProcessingDetailDialogComponent, {
			autoFocus: false,
			data: {
				postProcessing: this.postProcessing,
				preProcessing: this.preProcessing,
			},
			maxHeight: 'none',
			maxWidth: 'calc(100vw - 2rem)',
			panelClass: 'claim-usage-detail-dialog-panel',
			width: '44rem',
		});
	}

	openUploadDialog(): void {
		const dialogRef = this.dialog.open<
			ClaimUsageIngestionUploadDialogComponent,
			undefined,
			ClaimUsageIngestionUploadDialogResult | undefined
		>(ClaimUsageIngestionUploadDialogComponent, {
			maxWidth: 'calc(100vw - 2rem)',
			panelClass: 'claim-usage-upload-dialog-panel',
			width: '34rem',
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.uploadFile(result);
			}
		});
	}

	trackHistoryItemByJobId(
		_index: number,
		item: ClaimUsageIngestionHistoryItem,
	): string {
		return item.preProcessing.jobId;
	}

	private blockUnauthorizedAccess(): void {
		this.dialog.open(PermissionDeniedDialogComponent, {
			data: {
				message:
					'Somente usuarios super podem acessar a ingestao de sinistros.',
			},
		});
		void this.router.navigate(['/inicio']);
	}

	private buildBusinessKey({
		competence,
		organizationId,
	}: Pick<
		ClaimUsageIngestionUploadRequest,
		'competence' | 'organizationId'
	>): string {
		return `${organizationId}:${this.normalizeBusinessKeyCompetence(competence)}`;
	}

	private buildHistoryItem(
		preProcessing: ClaimUsageIngestionPreProcessing,
		postProcessing: ClaimUsageIngestionPostProcessing | null = null,
	): ClaimUsageIngestionHistoryItem {
		return {
			postProcessing,
			preProcessing,
		};
	}

	private buildPostProcessingFromStatus(
		statusResponse: ClaimUsageIngestionStatusResponse,
	): ClaimUsageIngestionPostProcessing {
		const processedRows =
			statusResponse.processedRows ?? statusResponse.processedLines ?? 0;
		const failedRows =
			statusResponse.failedRows ?? statusResponse.errorLines ?? 0;

		return {
			error: statusResponse.errorMessage ?? statusResponse.error,
			errorCode: statusResponse.errorCode,
			errorLines: failedRows,
			errorMessage: statusResponse.errorMessage ?? statusResponse.error,
			errorSummary: statusResponse.errorSummary,
			failedRows,
			finalStatus: statusResponse.status,
			finishedAt: statusResponse.finishedAt,
			lastUpdatedAt: statusResponse.lastUpdatedAt,
			processedLines: processedRows,
			processedRows,
			startedAt: statusResponse.startedAt,
			totalRows: statusResponse.totalRows ?? processedRows + failedRows,
		};
	}

	private buildPreProcessing(
		response: ClaimUsageIngestionAcceptedResponse,
		request: ClaimUsageIngestionUploadRequest,
	): ClaimUsageIngestionPreProcessing {
		const detectedMetadata = response.detectedMetadata;
		const originalFileName =
			request.file.name || detectedMetadata?.originalName || '';

		return {
			blobStatus: ClaimUsageIngestionBlobStatus.STORED,
			competence: request.competence,
			estimatedProcessingWindow: environment.claimUsageIngestionUseMock
				? 'Mock inicial'
				: 'Aguardando processamento',
			fileId: response.fileId,
			jobId: response.jobId,
			jobStatus: response.status,
			operator: request.operator,
			operatorName: request.operator,
			organizationId: request.organizationId,
			organizationName: request.organizationName,
			originalFileName: this.normalizeFileName(originalFileName),
			responsible: request.responsible,
			size: detectedMetadata?.fileSize ?? request.file.size,
			sourceFile: request.file,
			storagePath: detectedMetadata?.storagePath,
			uploadedAt: new Date().toISOString(),
			uploadedBy: request.responsible || request.uploadedBy,
		};
	}

	private canAccessClaimUsageIngestion(): boolean {
		return isSuperAdmin(this.authenticationsService.getSession());
	}

	private createUploadRequest({
		competence,
		file,
		operator,
	}: ClaimUsageIngestionUploadDialogResult): ClaimUsageIngestionUploadRequest {
		const responsible = this.getSessionResponsible();

		return {
			competence,
			file,
			operator,
			organizationId: this.getCurrentOrganizationId(),
			organizationName: this.orgSelectorService.getOrganizationName(),
			responsible,
			uploadedBy: responsible,
		};
	}

	private getCurrentOrganizationId(): string {
		return (
			this.routeOrganizationId ||
			this.getRouteOrganizationIdFromSnapshot() ||
			this.orgSelectorService.getOrganizationId() ||
			localStorage.getItem('org-id') ||
			''
		);
	}

	private getDownloadErrorMessage(error: unknown): Promise<string> | string {
		const fallbackMessage =
			'Nao foi possivel baixar o arquivo de origem desta carga.';

		if (!(error instanceof HttpErrorResponse)) {
			return fallbackMessage;
		}

		const responseError = error.error;

		if (typeof responseError === 'string' && responseError.trim()) {
			return responseError;
		}

		if (
			responseError &&
			typeof responseError === 'object' &&
			'message' in responseError &&
			typeof responseError.message === 'string' &&
			responseError.message.trim()
		) {
			return responseError.message;
		}

		if (responseError instanceof Blob) {
			return responseError
				.text()
				.then((text) => {
					const parsed = JSON.parse(text) as { message?: unknown };

					if (typeof parsed.message === 'string' && parsed.message.trim()) {
						return parsed.message;
					}

					return fallbackMessage;
				})
				.catch(() => fallbackMessage);
		}

		return fallbackMessage;
	}

	private getDownloadFileName(
		response: HttpResponse<Blob>,
		fallbackFileName: string,
	): string {
		const contentDisposition = response.headers.get('content-disposition');

		if (!contentDisposition) {
			return this.normalizeFileName(fallbackFileName);
		}

		const encodedFileName = /filename\*=UTF-8''([^;]+)/i.exec(
			contentDisposition,
		)?.[1];

		if (encodedFileName) {
			try {
				return this.normalizeFileName(decodeURIComponent(encodedFileName));
			} catch {
				return this.normalizeFileName(fallbackFileName);
			}
		}

		const quotedFileName = /filename="([^"]+)"/i.exec(contentDisposition)?.[1];
		const plainFileName = /filename=([^;]+)/i.exec(contentDisposition)?.[1];

		return this.normalizeFileName(
			(quotedFileName || plainFileName || fallbackFileName).trim(),
		);
	}

	private getPollingErrorForVisibleJob(): string {
		if (this.preProcessing?.jobId) {
			return this.pollingErrorsByJobId.get(this.preProcessing.jobId) ?? '';
		}

		return '';
	}

	private getRouteOrganizationIdFromSnapshot(): string {
		let currentRoute: ActivatedRoute | null = this.activatedRoute;

		while (currentRoute) {
			const paramMap = currentRoute.snapshot.paramMap;
			const organizationId =
				paramMap.get(RouteUtils.params.organizationId.value) ||
				paramMap.get('organizationId') ||
				paramMap.get('id');

			if (organizationId) {
				return organizationId;
			}

			currentRoute = currentRoute.parent;
		}

		return '';
	}

	private getRouteWithOrganizationParam(): ActivatedRoute | null {
		let currentRoute: ActivatedRoute | null = this.activatedRoute;

		while (currentRoute) {
			const paramMap = currentRoute.snapshot.paramMap;

			if (
				paramMap.has(RouteUtils.params.organizationId.value) ||
				paramMap.has('organizationId') ||
				paramMap.has('id')
			) {
				return currentRoute;
			}

			currentRoute = currentRoute.parent;
		}

		return null;
	}

	private getSessionResponsible(): string {
		const session: AuthenticationLoginResponseDto | null =
			this.authenticationsService.getSession();

		return session?.name?.trim() || session?.email?.trim() || '';
	}

	private getUploadErrorMessage(error: unknown): string {
		if (error instanceof HttpErrorResponse) {
			this.logUploadError(error);
			const responseError = error.error;

			if (typeof responseError === 'string' && responseError.trim()) {
				return responseError;
			}

			if (
				responseError &&
				typeof responseError === 'object' &&
				'message' in responseError &&
				typeof responseError.message === 'string' &&
				responseError.message.trim()
			) {
				return responseError.message;
			}
		}

		if (error instanceof Error) {
			// eslint-disable-next-line no-console -- Upload failures must be visible without exposing auth headers.
			console.error('Claim usage upload failed', {
				message: error.message,
				status: undefined,
				url: environment.claimUsageIngestionUploadApiUrl,
			});
		}

		return 'Nao foi possivel enfileirar o arquivo.';
	}

	private handlePollingError(jobId: string): void {
		this.inFlightPollingByJobId.delete(jobId);

		const historyItem = this.historyItems.find(
			(item) => item.preProcessing.jobId === jobId,
		);

		if (
			!jobId ||
			!historyItem ||
			!this.shouldStartPolling(historyItem.preProcessing.jobStatus)
		) {
			this.pollingErrorsByJobId.delete(jobId);

			return;
		}

		this.pollingErrorsByJobId.set(
			jobId,
			'Nao foi possivel atualizar o status do processamento.',
		);

		const pollingAttempts = this.pollingAttemptsByJobId.get(jobId) ?? 0;

		if (pollingAttempts > CLAIM_USAGE_INGESTION_MAX_POLLING_ATTEMPTS) {
			this.stopPolling(jobId);
		}
	}

	private handleStatusFinal(jobId: string): void {
		this.pollingErrorsByJobId.delete(jobId);
		this.releaseActiveBusinessKey(jobId);
		this.stopPolling(jobId);
	}

	private isFinalStatus(status: ClaimUsageIngestionJobStatus): boolean {
		return CLAIM_USAGE_INGESTION_FINAL_STATUSES.includes(status);
	}

	private isValidOperator(
		operator: string | null | undefined,
	): operator is ClaimUsageIngestionOperator {
		return operator === 'CNU' || operator === 'SULAMERICA';
	}

	private isValidUploadDialogResult(
		result: ClaimUsageIngestionUploadDialogResult,
	): boolean {
		return Boolean(
			result.file &&
				result.competence?.trim() &&
				this.isValidOperator(result.operator),
		);
	}

	private loadHistory(): void {
		const organizationId = this.getCurrentOrganizationId();

		this.isHistoryLoading = true;
		this.historyLoadError = '';

		this.claimUsageIngestionService
			.list(organizationId)
			.pipe(finalize(() => (this.isHistoryLoading = false)))
			.subscribe({
				error: () => {
					this.historyLoadError =
						'Nao foi possivel atualizar o historico de cargas.';
				},
				next: (items) => {
					const normalizedItems = items.map((item) =>
						this.normalizeHistoryItem(item),
					);
					const loadedJobIds = new Set(
						normalizedItems.map((item) => item.preProcessing.jobId),
					);
					const localItems = this.historyItems.filter(
						(item) => !loadedJobIds.has(item.preProcessing.jobId),
					);

					this.historyItems = [...localItems, ...normalizedItems];
					this.syncActiveBusinessKeysFromHistory(this.historyItems);
				},
			});
	}

	private logUploadError(error: HttpErrorResponse): void {
		const responseError = error.error;
		const responseMessage =
			responseError &&
			typeof responseError === 'object' &&
			'message' in responseError &&
			typeof responseError.message === 'string'
				? responseError.message
				: typeof responseError === 'string'
					? responseError
					: error.message;

		// eslint-disable-next-line no-console -- Upload failures must be visible without exposing auth headers.
		console.error('Claim usage upload failed', {
			message: responseMessage,
			status: error.status,
			url: error.url,
		});
	}

	private normalizeBusinessKeyCompetence(competence: string): string {
		const trimmedCompetence = competence.trim();
		const slashMatch = /^(\d{1,2})\/(\d{4})$/.exec(trimmedCompetence);

		if (slashMatch) {
			const [, month, year] = slashMatch;

			return `${year}-${month.padStart(2, '0')}`;
		}

		const dashMatch = /^(\d{4})-(\d{1,2})$/.exec(trimmedCompetence);

		if (dashMatch) {
			const [, year, month] = dashMatch;

			return `${year}-${month.padStart(2, '0')}`;
		}

		return trimmedCompetence;
	}

	private normalizeFileName(fileName: string): string {
		if (!/[ÃÂ]/.test(fileName)) {
			return fileName;
		}

		try {
			const bytes = Uint8Array.from(
				Array.from(fileName, (character) => character.charCodeAt(0)),
			);

			return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
		} catch {
			return fileName;
		}
	}

	private normalizeHistoryItem(
		item: ClaimUsageIngestionHistoryItem,
	): ClaimUsageIngestionHistoryItem {
		const selectedOrganizationName =
			this.orgSelectorService.getOrganizationName();
		const jobStatus = this.normalizeHistoryJobStatus(item);
		const postProcessing = item.postProcessing
			? {
					...item.postProcessing,
					finalStatus: jobStatus,
				}
			: item.postProcessing;

		return {
			...item,
			postProcessing,
			preProcessing: {
				...item.preProcessing,
				jobStatus,
				operatorName:
					item.preProcessing.operatorName || item.preProcessing.operator || '',
				organizationName:
					item.preProcessing.organizationName || selectedOrganizationName,
				originalFileName: this.normalizeFileName(
					item.preProcessing.originalFileName,
				),
			},
		};
	}

	private normalizeHistoryJobStatus(
		item: ClaimUsageIngestionHistoryItem,
	): ClaimUsageIngestionJobStatus {
		if (
			item.preProcessing.jobStatus ===
				ClaimUsageIngestionJobStatus.PROCESSING &&
			item.postProcessing?.finishedAt &&
			item.postProcessing?.errorMessage
		) {
			return ClaimUsageIngestionJobStatus.FAILED;
		}

		return item.preProcessing.jobStatus;
	}

	private openHistoryDetailDialog(
		item: ClaimUsageIngestionHistoryItem,
		errors: ClaimUsageIngestionErrorItem[] = [],
	): void {
		this.dialog.open(ClaimUsageIngestionProcessingDetailDialogComponent, {
			autoFocus: false,
			data: {
				...(errors.length ? { errors } : {}),
				postProcessing: item.postProcessing,
				preProcessing: item.preProcessing,
			},
			maxHeight: 'none',
			maxWidth: 'calc(100vw - 2rem)',
			panelClass: 'claim-usage-detail-dialog-panel',
			width: '44rem',
		});
	}

	private releaseActiveBusinessKey(jobId: string): void {
		const businessKey = this.businessKeyByJobId.get(jobId);

		if (businessKey) {
			this.businessKeyByJobId.delete(jobId);

			if (!Array.from(this.businessKeyByJobId.values()).includes(businessKey)) {
				this.releaseBusinessKey(businessKey);
			}
		}
	}

	private releaseBusinessKey(businessKey: string): void {
		this.activeBusinessKeys.delete(businessKey);

		if (this.blockedUploadBusinessKey === businessKey) {
			this.blockedUploadBusinessKey = '';
			this.blockedUploadMessage = '';
		}
	}

	private resetOrganizationSensitiveState(): void {
		this.stopAllPolling();
		this.activeBusinessKeys.clear();
		this.blockedUploadBusinessKey = '';
		this.blockedUploadMessage = '';
		this.businessKeyByJobId.clear();
		this.historyItems = [];
		this.historyLoadError = '';
		this.pollingAttemptsByJobId.clear();
		this.pollingErrorsByJobId.clear();
		this.postProcessing = null;
		this.preProcessing = null;
		this.uploadError = '';
	}

	private saveBlob(blob: Blob, fileName: string): void {
		const objectUrl = URL.createObjectURL(blob);
		const anchor = document.createElement('a');

		anchor.href = objectUrl;
		anchor.download = fileName;

		try {
			anchor.click();
		} finally {
			URL.revokeObjectURL(objectUrl);
		}
	}

	private shouldApplyStatusResponse(
		jobId: string,
		statusResponse: ClaimUsageIngestionStatusResponse,
	): boolean {
		const currentPostProcessing =
			this.historyItems.find((item) => item.preProcessing.jobId === jobId)
				?.postProcessing ??
			(this.preProcessing?.jobId === jobId ? this.postProcessing : null);
		const currentLastUpdatedAt = currentPostProcessing?.lastUpdatedAt;

		if (!currentLastUpdatedAt || !statusResponse.lastUpdatedAt) {
			return true;
		}

		return (
			new Date(statusResponse.lastUpdatedAt).getTime() >=
			new Date(currentLastUpdatedAt).getTime()
		);
	}

	private shouldLoadErrors(item: ClaimUsageIngestionHistoryItem): boolean {
		return Boolean(
			item.postProcessing?.failedRows ||
				item.postProcessing?.errorMessage ||
				item.preProcessing.jobStatus === ClaimUsageIngestionJobStatus.FAILED,
		);
	}

	private shouldStartPolling(status: ClaimUsageIngestionJobStatus): boolean {
		return CLAIM_USAGE_INGESTION_ACTIVE_STATUSES.includes(status);
	}

	private startPolling(jobId: string, businessKey: string): void {
		if (!jobId) {
			this.releaseBusinessKey(businessKey);

			return;
		}

		this.stopPolling(jobId);
		this.businessKeyByJobId.set(jobId, businessKey);
		this.pollingAttemptsByJobId.set(jobId, 0);

		const pollingSubscription = timer(
			CLAIM_USAGE_INGESTION_POLLING_INTERVAL_MS,
			CLAIM_USAGE_INGESTION_POLLING_INTERVAL_MS,
		).subscribe(() => {
			if (this.inFlightPollingByJobId.has(jobId)) {
				return;
			}

			const pollingAttempts = (this.pollingAttemptsByJobId.get(jobId) ?? 0) + 1;

			this.pollingAttemptsByJobId.set(jobId, pollingAttempts);

			if (pollingAttempts > CLAIM_USAGE_INGESTION_MAX_POLLING_ATTEMPTS) {
				this.handlePollingError(jobId);

				return;
			}

			this.inFlightPollingByJobId.add(jobId);
			this.claimUsageIngestionService.getStatus(jobId).subscribe({
				error: () => {
					this.inFlightPollingByJobId.delete(jobId);
					this.handlePollingError(jobId);
				},
				next: (statusResponse) => {
					this.inFlightPollingByJobId.delete(jobId);
					this.pollingErrorsByJobId.delete(jobId);

					if (!this.shouldApplyStatusResponse(jobId, statusResponse)) {
						if (!this.shouldStartPolling(statusResponse.status)) {
							this.stopPollingInactiveJob(jobId, statusResponse.status);
						}

						return;
					}

					const historyItem = this.historyItems.find(
						(item) => item.preProcessing.jobId === jobId,
					);
					const updatedPreProcessing = {
						...(this.preProcessing?.jobId === jobId
							? this.preProcessing
							: historyItem?.preProcessing),
						jobStatus: statusResponse.status,
					} as ClaimUsageIngestionPreProcessing;
					const updatedPostProcessing =
						this.buildPostProcessingFromStatus(statusResponse);

					if (this.preProcessing?.jobId === jobId) {
						this.preProcessing = updatedPreProcessing;
						this.postProcessing = updatedPostProcessing;
					}

					if (historyItem || this.preProcessing?.jobId === jobId) {
						this.upsertHistoryItem(
							this.buildHistoryItem(
								updatedPreProcessing,
								updatedPostProcessing,
							),
						);
					}

					if (this.isFinalStatus(statusResponse.status)) {
						if (statusResponse.status === ClaimUsageIngestionJobStatus.FAILED) {
							this.pollingErrorsByJobId.set(
								jobId,
								statusResponse.errorMessage ||
									statusResponse.error ||
									'Processamento finalizado com erro.',
							);
						}

						this.handleStatusFinal(jobId);

						return;
					}

					if (!this.shouldStartPolling(statusResponse.status)) {
						this.stopPollingInactiveJob(jobId, statusResponse.status);
					}
				},
			});
		});

		this.pollingSubscriptionsByJobId.set(jobId, pollingSubscription);
	}

	private stopAllPolling(): void {
		this.pollingSubscriptionsByJobId.forEach((subscription) => {
			subscription.unsubscribe();
		});
		this.pollingSubscriptionsByJobId.clear();
		this.inFlightPollingByJobId.clear();
	}

	private stopPolling(jobId: string): void {
		this.pollingSubscriptionsByJobId.get(jobId)?.unsubscribe();
		this.pollingSubscriptionsByJobId.delete(jobId);
		this.pollingAttemptsByJobId.delete(jobId);
		this.inFlightPollingByJobId.delete(jobId);
	}

	private stopPollingInactiveJob(
		jobId: string,
		status: ClaimUsageIngestionJobStatus,
	): void {
		this.pollingErrorsByJobId.delete(jobId);
		this.stopPolling(jobId);

		if (this.isFinalStatus(status)) {
			this.releaseActiveBusinessKey(jobId);
		}
	}

	private syncActiveBusinessKeysFromHistory(
		items: ClaimUsageIngestionHistoryItem[],
	): void {
		items.forEach((item) => {
			if (
				item.preProcessing.jobId &&
				this.shouldStartPolling(item.preProcessing.jobStatus)
			) {
				const businessKey = this.buildBusinessKey({
					competence: item.preProcessing.competence,
					organizationId: item.preProcessing.organizationId,
				});

				this.activeBusinessKeys.add(businessKey);
				this.businessKeyByJobId.set(item.preProcessing.jobId, businessKey);

				if (
					!environment.claimUsageIngestionUseMock &&
					!this.pollingSubscriptionsByJobId.has(item.preProcessing.jobId)
				) {
					this.startPolling(item.preProcessing.jobId, businessKey);
				}
			}
		});
	}

	private uploadFile(result: ClaimUsageIngestionUploadDialogResult): void {
		if (!this.isValidUploadDialogResult(result)) {
			this.uploadError =
				'Informe operadora, competencia e arquivo antes de enviar.';

			return;
		}

		const request = this.createUploadRequest(result);
		const businessKey = this.buildBusinessKey(request);

		const shouldBlockDuplicateUpload = false;

		if (
			shouldBlockDuplicateUpload &&
			this.activeBusinessKeys.has(businessKey)
		) {
			this.blockedUploadBusinessKey = businessKey;
			this.blockedUploadMessage =
				'Upload bloqueado: ja existe job ativo para este cliente e competencia.';

			return;
		}

		this.activeBusinessKeys.add(businessKey);
		this.blockedUploadBusinessKey = '';
		this.blockedUploadMessage = '';
		this.isUploading = true;
		this.postProcessing = null;
		this.uploadError = '';

		this.claimUsageIngestionService
			.upload(request)
			.pipe(finalize(() => (this.isUploading = false)))
			.subscribe({
				error: (error: unknown) => {
					this.uploadError = this.getUploadErrorMessage(error);
					this.releaseBusinessKey(businessKey);
				},
				next: (acceptedResponse) => {
					this.preProcessing = this.buildPreProcessing(
						acceptedResponse,
						request,
					);
					this.upsertHistoryItem(
						this.buildHistoryItem(this.preProcessing, null),
					);

					this.businessKeyByJobId.set(acceptedResponse.jobId, businessKey);

					if (this.shouldStartPolling(acceptedResponse.status)) {
						this.startPolling(acceptedResponse.jobId, businessKey);

						return;
					}

					if (this.isFinalStatus(acceptedResponse.status)) {
						this.releaseActiveBusinessKey(acceptedResponse.jobId);
					}
				},
			});
	}

	private upsertHistoryItem(item: ClaimUsageIngestionHistoryItem): void {
		const index = this.historyItems.findIndex(
			(historyItem) =>
				historyItem.preProcessing.jobId === item.preProcessing.jobId,
		);

		if (index === -1) {
			this.historyItems = [item, ...this.historyItems];

			return;
		}

		this.historyItems = [
			...this.historyItems.slice(0, index),
			item,
			...this.historyItems.slice(index + 1),
		];
	}

	private watchOrganizationRouteContext(): void {
		const routeWithOrganizationParam = this.getRouteWithOrganizationParam();

		if (!routeWithOrganizationParam) {
			this.loadHistory();

			return;
		}

		this.routeOrganizationSubscription =
			routeWithOrganizationParam.paramMap.subscribe(() => {
				const nextOrganizationId = this.getRouteOrganizationIdFromSnapshot();

				if (!nextOrganizationId) {
					this.routeOrganizationId = '';
					this.loadHistory();

					return;
				}

				if (
					this.routeOrganizationId &&
					this.routeOrganizationId !== nextOrganizationId
				) {
					this.resetOrganizationSensitiveState();
				}

				this.routeOrganizationId = nextOrganizationId;
				this.loadHistory();
			});
	}
}
